import * as R from 'ramda';
import React, { useContext, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { Headline } from '@xingternal/typography';
import { Column } from '@xingternal/xing-grid';

import { JobPostingModule } from '@/components/job-posting-module';
import { JDPModuleName } from '@/components/job-posting/types';
import { SimilarJob } from '@/components/similar-job';
import { SimilarJobsContext } from '@/contexts';

import { FOOTER_SIMILAR_JOBS_COUNT } from '../../constants';
import { useJobPostingModuleDispatcher } from '../../contexts';
import { ScrollTrackingModuleWrapper } from '../scroll-tracking-module-wrapper';

import styles from './similar-jobs-module.css';

export const SimilarJobsModule: React.FC<{
  moduleName: JDPModuleName;
}> = ({ moduleName }) => {
  const { loading, error, footerJobs } = useContext(SimilarJobsContext);
  const dispatch = useJobPostingModuleDispatcher();

  const isEmpty = (!loading && R.isEmpty(footerJobs)) || error;

  useEffect(() => {
    if (!loading) {
      dispatch({ type: 'UPDATE_MODULE_STATUS', moduleName, status: isEmpty ? 'HIDDEN' : 'VISIBLE' });
    }
  }, [dispatch, isEmpty, loading, moduleName]);

  if (isEmpty) {
    return null;
  }

  const similarJobs = loading
    ? Array(FOOTER_SIMILAR_JOBS_COUNT).fill({})
    : footerJobs.filter(({ job }) => job?.__typename === 'VisibleJob');

  return (
    <ScrollTrackingModuleWrapper
      moduleTrackingName={moduleName}
      scrollOptions={{
        bottom: '-330px',
        threshold: 0,
      }}
      trackInView
    >
      <JobPostingModule
        className={styles.wrapper}
        data-testid="job-posting-similar-jobs-module"
        data-cy="job-posting-similar-jobs-module"
      >
        <Column size={12} className={styles.title}>
          <Headline size="xlarge" sizeWide="xxlarge" noMargin>
            <FormattedMessage id="JOB_POSTING_SIMILAR_JOBS_H" />
          </Headline>
        </Column>
        {similarJobs.map(
          ({ job, trackingToken, position }, index) =>
            (loading || job) && (
              <Column
                size={12}
                sizeConfined={6}
                sizeWide={4}
                key={job?.id ?? index}
                className={styles.similarJobColumn}
                data-cy="similar-job"
              >
                <SimilarJob
                  isLoading={loading}
                  job={job}
                  trackingParam="jb_20"
                  trackingToken={trackingToken}
                  position={position}
                />
              </Column>
            )
        )}
      </JobPostingModule>
    </ScrollTrackingModuleWrapper>
  );
};
